import { useState, type FormEvent } from 'react'
import { clipFactoryAPI, clipFactoryErrorText, type FindClipsResult } from './clipFactoryApi'

interface FindClipsProps {
  videoId: string
  /** Claude is set up; without it the search is by keyword. */
  aiAvailable: boolean
  onFound: (result: FindClipsResult) => void
}

/** "Added 2 clips · 1 already in your clips" from a search's result. */
export const foundSentence = (r: Pick<FindClipsResult, 'added' | 'already'>): string => {
  const added = r.added ? `Added ${r.added} ${r.added === 1 ? 'clip' : 'clips'}` : 'No new clips found'
  return r.already ? `${added} · ${r.already} already in your clips` : added
}

/**
 * Find clips beyond the suggestions: the moments matching what you ask for, or
 * a clip around every trade you talk through. New clips join the list below,
 * each saying what it was found for.
 */
const FindClips = ({ videoId, aiAvailable, onFound }: FindClipsProps) => {
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState<'request' | 'trades' | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = async (what: 'request' | 'trades') => {
    setBusy(what)
    setMessage(null)
    setError(null)
    try {
      const r = await clipFactoryAPI.findClips(videoId, what === 'trades' ? { trades: true } : { request: query })
      onFound(r)
      setMessage([foundSentence(r), r.notice].filter(Boolean).join('. '))
      if (what === 'request' && r.added) setQuery('')
    } catch (e) {
      setError(clipFactoryErrorText(e, "Couldn't find clips."))
    } finally {
      setBusy(null)
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (query.trim()) run('request')
  }

  return (
    <div className="mt-3 space-y-2" data-testid="find-clips">
      <form onSubmit={submit} className="flex flex-wrap gap-2">
        <label htmlFor="find-clips-query" className="sr-only">
          Find clips about
        </label>
        <input
          id="find-clips-query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          maxLength={200}
          placeholder='Find clips about… e.g. "where I talk about the ETF"'
          className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <button
          type="submit"
          disabled={busy !== null || !query.trim()}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy === 'request' ? 'Finding…' : 'Find'}
        </button>
        <button
          type="button"
          onClick={() => run('trades')}
          disabled={busy !== null}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          {busy === 'trades' ? 'Finding…' : 'Find my trade calls'}
        </button>
      </form>
      <p className="text-xs text-gray-500">
        {aiAvailable ? 'Searched by AI from the transcript.' : 'Searched by keyword in the transcript (no AI).'} Trade
        calls finds every coin and direction you say, with no AI.
      </p>
      {message && (
        <p className="text-sm text-gray-700" role="status">
          {message}
        </p>
      )}
      {error && <p className="text-sm text-danger-600">{error}</p>}
    </div>
  )
}

export default FindClips
